import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { List } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import WorkflowsIcon from 'assets/icons/icon-content-flow.svg?react'
import MapSearchIcon from 'assets/icons/icon-maps-map-search.svg?react'
import MapNewIcon from 'assets/icons/icon-maps-map-new.svg?react'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import DataObservatoryIcon from 'assets/icons/data-observatory-s.svg?react'
import ProductsDataExplorerIcon from 'assets/icons/icon-products-data-explorer.svg?react'
import { useAppDispatch, useAppSelector } from 'app/state'
import { fetchDemoMapsRequest } from 'features/maps/state/mapsSlice'
import OnboardingChecklistTask from './OnboardingChecklistTask'
import useOnboardingChecklist, { OnboardingAction } from '../utils/useOnboardingChecklist'
import useDemoMaps from 'features/maps/utils/useDemoMaps'
import MapsHardQuotaModal from 'features/maps/ui/MapsHardQuotaModal'
import { useHardQuotaModalController } from 'features/quotas/ui/WouldExceedQuotaDialog'
import { useAccountQuotas } from 'features/quotas/ui/hooks'
import { QuotaUxTrigger } from 'features/hubspot/types'
import OnboardingChecklistVideos from './OnboardingChecklistVideos'
import { Video } from '../types'
import OnboardingChecklistVideoDialog from './OnboardingChecklistVideoDialog'
import { useCartoDW } from 'features/connections/hooks/connectionsHooks'

const FIRST_WORKFLOW_TUTORIAL_URL =
  'https://docs.carto.com/getting-started/quickstart-guides/creating-your-first-workflow'

const FIRST_MAP_TUTORIAL_URL = 'https://docs.carto.com/getting-started/quickstart-guides/creating-your-first-map'

export function OnboardingChecklistContent() {
  const onboardingChecklistTasks = useAppSelector((state) => state.onboardingChecklist.tasks)
  const demoMaps = useAppSelector((state) => state.maps.demoMaps)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { toggleOnboardingChecklist, completeOnboardingChecklist } = useOnboardingChecklist()
  const { openDemoMap, openDemoMapAction } = useDemoMaps()
  const { mapsQuota } = useAccountQuotas()
  const hardQuotaModalController = useHardQuotaModalController(mapsQuota, openDemoMapAction)
  const { isAvailable: isCartoDWAvailable } = useCartoDW()
  const { showConnectionsBanner } = useCartoDW()
  const taskDisabled = !isCartoDWAvailable ? showConnectionsBanner : false

  const [videoSelected, setVideoSelected] = useState<Video>(Video.WORKSPACE)
  const [showVideoDialog, setShowVideoDialog] = useState(false)

  const creatingDemoMapsIds = useAppSelector((state) => state.maps.creatingDemoMapsIds)
  const creatingDemoMap = creatingDemoMapsIds.length > 0

  const navigateAndCloseChecklist = useCallback(
    (path: string) => {
      navigate(path)
      toggleOnboardingChecklist()
    },
    [navigate, toggleOnboardingChecklist]
  )

  const onBuildFirstMapClick = useCallback(() => {
    completeOnboardingChecklist(OnboardingAction.BuildFirstMap)
    window.open(FIRST_MAP_TUTORIAL_URL, '_blank')
  }, [completeOnboardingChecklist])

  const onBuildFirstWorkflowClick = useCallback(() => {
    completeOnboardingChecklist(OnboardingAction.BuildFirstWorkflow)
    window.open(FIRST_WORKFLOW_TUTORIAL_URL, '_blank')
  }, [completeOnboardingChecklist])

  const onInviteColleaguesClick = useCallback(() => {
    navigateAndCloseChecklist('/settings/users')
  }, [navigateAndCloseChecklist])

  const onBrowseDataExplorerClick = useCallback(() => {
    navigateAndCloseChecklist('/connections/create')
  }, [navigateAndCloseChecklist])

  const handleCatalogClick = useCallback(() => {
    navigateAndCloseChecklist('/catalog')
  }, [navigateAndCloseChecklist])

  const onCheckDemoMapClick = useCallback(() => {
    if (demoMaps.length > 0) {
      const map = demoMaps[0]
      openDemoMap(map)
    }
  }, [demoMaps, openDemoMap])

  const tasksData = [
    {
      id: 'browse_data_explorer',
      title: <FormattedMessage id="home.onboarding.checklist.tasks.browseDataExplorer" />,
      icon: <ProductsDataExplorerIcon />,
      onClick: onBrowseDataExplorerClick,
      disabled: false
    },
    {
      id: 'check_demo_map',
      title: creatingDemoMap ? (
        <FormattedMessage id="maps.creatingDemoMap" />
      ) : (
        <FormattedMessage id="home.onboarding.checklist.tasks.checkDemoMap" />
      ),
      icon: <MapSearchIcon />,
      onClick: onCheckDemoMapClick,
      loading: creatingDemoMap,
      disabled: false
    },
    {
      id: 'build_first_map',
      title: <FormattedMessage id="home.onboarding.checklist.tasks.buildFirstMap" />,
      icon: <MapNewIcon />,
      onClick: onBuildFirstMapClick,
      disabled: taskDisabled
    },
    {
      id: 'build_first_workflow',
      title: <FormattedMessage id="home.onboarding.checklist.tasks.buildFirstWorkflow" />,
      icon: <WorkflowsIcon />,
      onClick: onBuildFirstWorkflowClick,
      disabled: taskDisabled
    },
    {
      id: 'browse_do_catalog',
      title: <FormattedMessage id="home.onboarding.checklist.tasks.browseDoCatalog" />,
      icon: <DataObservatoryIcon />,
      onClick: handleCatalogClick,
      disabled: false
    },
    {
      id: 'invite_your_colleagues',
      title: <FormattedMessage id="home.onboarding.checklist.tasks.inviteColleagues" />,
      icon: <PeopleOutlineIcon />,
      onClick: onInviteColleaguesClick,
      disabled: false
    }
  ]

  const tasksWithCompletion = onboardingChecklistTasks?.map((task) => {
    const taskData = tasksData.find((data) => data.id === task.id)
    return {
      ...task,
      ...taskData
    }
  })

  const orderedTasks = tasksWithCompletion?.sort((a, b) => {
    if (a.completed && !b.completed) return -1
    if (!a.completed && b.completed) return 1

    return tasksData.findIndex((data) => data.id === a.id) - tasksData.findIndex((data) => data.id === b.id)
  })

  useEffect(() => {
    if (demoMaps.length === 0) {
      dispatch(fetchDemoMapsRequest())
    }
  }, [demoMaps, dispatch])

  const handleClickVideo = (video: Video) => {
    setVideoSelected(video)
    setShowVideoDialog(true)
  }

  return (
    <>
      <List sx={{ p: 2, pt: 0 }}>
        {orderedTasks?.map((task, i) => {
          return <OnboardingChecklistTask {...task} key={i} />
        })}
      </List>
      <OnboardingChecklistVideos videoTypeSelected={videoSelected} onClickVideo={(video) => handleClickVideo(video)} />
      <OnboardingChecklistVideoDialog
        video={videoSelected}
        open={showVideoDialog}
        onClose={() => setShowVideoDialog(false)}
      />
      <MapsHardQuotaModal {...hardQuotaModalController.props} variant="create" quotaUxTrigger={QuotaUxTrigger.NewMap} />
    </>
  )
}

export default OnboardingChecklistContent
